import Phaser from 'phaser';
import { FIREFLY_NEST } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { BuildingSlot, ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';
import { WALL_X, WALL_WIDTH } from '../../config';

export class FireflyNest {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'firefly_nest';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private attackTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    bonusProjectiles: number = 0;
    bonusAoeRadius: number = 0;
    firePercent: number = 0;
    fireDuration: number = 0;
    fireEnabled: boolean = true; // Fireflies always burn
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.sprite = scene.add.sprite(x, y, 'building_firefly');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.firePercent = FIREFLY_NEST.firePercent;
        this.fireDuration = FIREFLY_NEST.fireDuration;
        this.draw();
    }

    getDamage(): number {
        return FIREFLY_NEST.baseDamage * this.stackCount * this.bonusDamageMult * this.globalDamageMult;
    }

    getFireflyCount(): number {
        return FIREFLY_NEST.fireflyCount + this.bonusProjectiles;
    }

    getAoeRadius(): number {
        return FIREFLY_NEST.aoeRadius + this.bonusAoeRadius;
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.extraProjectiles !== undefined) this.bonusProjectiles += effect.extraProjectiles;
        if (effect.aoeRadiusBonus !== undefined) this.bonusAoeRadius += effect.aoeRadiusBonus;
        if (effect.firePercentBonus !== undefined) this.firePercent += effect.firePercentBonus;
        if (effect.fireDurationBonus !== undefined) this.fireDuration += effect.fireDurationBonus;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const validEnemies = this.getValidEnemies(enemies);
            if (validEnemies.length > 0) {
                this.attackTimer = FIREFLY_NEST.attackInterval / this.attackSpeedMult;
                this.fireVolley(validEnemies);
            }
        }
        this.draw();
    }

    private getValidEnemies(enemies: Enemy[]): Enemy[] {
        const range = FIREFLY_NEST.range;
        const wallEdge = WALL_X + WALL_WIDTH;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            if (enemy.x - wallEdge > range) continue;
            candidates.push(enemy);
        }
        return candidates;
    }

    private fireVolley(validEnemies: Enemy[]): void {
        const count = this.getFireflyCount();
        const damage = this.getDamage();

        // Shuffle enemies so the fireflies pick unique targets if possible
        const shuffled = [...validEnemies].sort(() => Math.random() - 0.5);

        for (let i = 0; i < count; i++) {
            // Assign a target in a round-robin way
            const target = shuffled[i % shuffled.length];

            const proj = new Projectile(this.scene, this.x, this.y - 12, {
                damage,
                speed: FIREFLY_NEST.projectileSpeed,
                piercing: false,
                projectileType: 'firefly',
                targetX: target.x,
                targetY: target.y,
                target: target,
                aoeRadius: this.getAoeRadius(),
                fireDamage: Math.round(damage * this.firePercent),
                fireDuration: this.fireDuration,
            });
            this.projectiles.push(proj);
        }
    }

    draw(): void {
        this.sprite.setTexture('building_firefly');
        this.graphics.clear();

        if (this.level > 1) {
            this.graphics.fillStyle(0xff8800, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
    }
}
